import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { EMPTY } from 'rxjs';
import { catchError, timeout } from 'rxjs/operators';
import { LocalStorageService } from './local-storage.service';
import { AccountClient } from '../api-client';
import { JWTTokenService } from './jwt-token.service';
import { UserStore } from '@/account/stores/user.store';
import { TrackingService } from './tracking.service';

/** Written after storage is cleared so other tabs receive a keyed `storage` event (ALT-8260). */
export const LOGOUT_SENTINEL_KEY = 'logout_at';
/** Bounds the best-effort server call; a hung request would otherwise pin LoadingService's counter. */
export const LOGOUT_REQUEST_TIMEOUT_MS = 10_000;

export type LogoutReason =
  | 'user'
  | 'timeout'
  | 'other-tab'
  | 'interceptor'
  | 'guard'
  | 'refresh_failed';

export interface LogoutOptions {
  /**
   * Call POST /api/account/logout so the server drops the .AspNet.Altorney cookie carrying
   * company claims (ALT-3811). Pass false when another tab has already done it.
   */
  notifyServer?: boolean;
  /** Why the session ends; used for diagnostics only. */
  reason?: LogoutReason;
}

@Injectable({
  providedIn: 'root',
})
export class LogOutService {
  protected userStore = inject(UserStore);

  constructor(
    private readonly router: Router,
    private readonly accountClient: AccountClient,
    private readonly jwtTokenService: JWTTokenService,
    private readonly localStorageService: LocalStorageService,
    private readonly tracking: TrackingService
  ) {}

  /**
   * Ends the session locally right away and navigates to login. The server call is fire-and-forget:
   * it must be made (cookie), but a failed request (laptop wake, offline) must never leave the tab
   * signed in.
   */
  logout(next?: string, options: LogoutOptions = {}): Promise<boolean> {
    const reason = options.reason ?? 'user';
    const notifyServer = options.notifyServer ?? true;
    this.tracking.trackSession('logout', {
      trigger: reason,
      notifyServer,
      next,
    });
    if (notifyServer) {
      // Subscribe before clearing storage: interceptors read state synchronously on subscribe.
      this.accountClient
        .logout()
        .pipe(
          timeout(LOGOUT_REQUEST_TIMEOUT_MS),
          catchError((err) => {
            this.tracking.trackSession('logout_server_failed', {
              trigger: reason,
              status: err?.status,
              message: err?.message ?? String(err),
            });
            return EMPTY;
          })
        )
        .subscribe();
    }

    this.jwtTokenService.logout();
    this.userStore.clear();
    this.localStorageService.set(LOGOUT_SENTINEL_KEY, Date.now().toString());

    return this.router
      .navigate(['/', 'auth', 'login'], {
        replaceUrl: true,
        queryParams: { next },
      })
      .catch((err) => {
        this.tracking.captureException(err, {
          extra: { message: 'Logout navigation failed' },
        });
        return false;
      });
  }
}
